import { IconButton, Stack } from '@mui/material';
import { Subsidiary, NetSuiteSubsidiary, INTEGRATION_PROVIDERS } from '@vy/types';
import { isEmptyString, isNotEmptyString } from '@vy/utils';
import { FC, useState } from 'react';
import { VyAutocompleteSelect } from '../../../../../../../components/new-design/inputs/VyAutocompleteSelect';
import { useUpdateSubsidiary } from '../../../../../../../hooks/requests/use-update-subsidiary';
import { runMutation } from '../../../../../../../utils/vy-mutation';
import { VyIcon } from '@vy/ui-design-system';
import { UpdateSubsidiaryRequestPayload } from '@vy/api-definition';

type SyncErpSubsidiaryItemProps = {
  vayuSubsidiary: Subsidiary;
  nsSubsidiaries: NetSuiteSubsidiary[];
  allSubsidiaries: Subsidiary[];
};

export const SyncErpSubsidiaryItem: FC<SyncErpSubsidiaryItemProps> = ({
  vayuSubsidiary,
  nsSubsidiaries,
  allSubsidiaries,
}) => {
  const [selectedNsSubsidiary, setSelectedNsSubsidiary] = useState<NetSuiteSubsidiary | null>(null);
  const updateMutation = useUpdateSubsidiary(vayuSubsidiary.id);

  const handleSyncToggle = async () => {
    const isCurrentlySynced = isNotEmptyString(vayuSubsidiary.erp?.integrationId);
    const newErpId = isNotEmptyString(vayuSubsidiary.erp?.integrationId)
      ? undefined
      : selectedNsSubsidiary?.id;
    const updateSubsidiaryPayload = buildUpdatePayload(vayuSubsidiary, newErpId);
    const toastMessages = getToastMessages(isCurrentlySynced);

    await runMutation(updateMutation, updateSubsidiaryPayload, toastMessages);
  };

  const getNsSubsidiaryValue = () => {
    const currentlySyncedNsSubsidiary = nsSubsidiaries.find(
      (nsSubsidiary) => nsSubsidiary.id === vayuSubsidiary.erp?.integrationId,
    );

    return currentlySyncedNsSubsidiary ?? selectedNsSubsidiary ?? null;
  };

  const isSynced = isNotEmptyString(vayuSubsidiary.erp?.integrationId);
  const nsSubsidiaryValue = getNsSubsidiaryValue();

  return (
    <Stack direction="row" spacing={3} key={vayuSubsidiary.id} sx={{
      width: "100%"
    }}>
      <VyAutocompleteSelect
        label="Vayu Subsidiary"
        options={allSubsidiaries}
        value={vayuSubsidiary}
        getOptionLabel={(option) => option.name}
        disabled={true}
        sx={{
          '.MuiTextField-root': {
            width: '220px',
          },
        }}
      />
      <IconButton
        size="small"
        onClick={handleSyncToggle}
        sx={{
          top: 11,
          alignSelf: 'center',
        }}
      >
        <VyIcon icon={isSynced ? 'Link' : 'Unlink'} />
      </IconButton>
      <VyAutocompleteSelect
        label="NS Subsidiary"
        options={nsSubsidiaries}
        value={nsSubsidiaryValue}
        onOptionSelect={(option) => setSelectedNsSubsidiary(option as NetSuiteSubsidiary)}
        getOptionLabel={(option) => option?.name ?? ''}
        disabled={isSynced}
        sx={{
          '.MuiTextField-root': {
            width: '220px',
          },
        }}
      />
    </Stack>
  );
};

function getToastMessages(isCurrentlySynced: boolean) {
  return {
    pending: isCurrentlySynced ? 'Unsyncing...' : 'Syncing...',
    success: isCurrentlySynced ? 'Unsynced successfully' : 'Synced successfully',
    error: isCurrentlySynced ? 'Failed to unsync' : 'Failed to sync',
  };
}

function buildUpdatePayload(
  vayuSubsidiary: Subsidiary,
  newErpId: string | undefined,
): UpdateSubsidiaryRequestPayload {
  const erp = buildErp(newErpId);

  return {
    subsidiary: {
      ...vayuSubsidiary,
      erp,
    },
  };
}

function buildErp(newErpId: string | undefined) {
  if (isEmptyString(newErpId)) {
    return undefined;
  }

  return {
    integration: INTEGRATION_PROVIDERS.netSuite,
    integrationId: newErpId,
  };
}
